import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import {
    setAutohideChatListState,
    setChatNotificationsState,
    setHideChatListOnLoadState,
    setNotifications,
    setQRCodeLinksState,
} from '../../../../redux/features/app/appSlice';

interface ChatSettingsProps {
    isDarkMode: boolean;
    chatMode: string;
    setIsDarkMode: () => void;
    changeChatMode: (mode: string) => void;
    toggleSettings: () => void;
}

export const ChatSettings: React.FC<ChatSettingsProps> = ({
    isDarkMode,
    chatMode,
    setIsDarkMode,
    changeChatMode,
    toggleSettings,
}) => {
    const dispatch = useAppDispatch();
    const { isChatNotificationsEnabled, isHideChatListOnLoadEnabled, isAutohideChatListEnabled, isQRCodeEnabled } =
        useAppSelector((state: RootState) => state.app);

    return (
        <aside className="flex">
            {/* Sidebar */}
            <div className="absolute md:relative h-screen w-full md:w-80 overflow-y-auto py-1 border-l border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center m-2 ml-1">
                    <button
                        className="inline-flex rounded-lg p-1 hover:bg-slate-200 dark:hover:bg-slate-800"
                        title="Close settings panel"
                        onClick={() => toggleSettings()}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6 text-primary-300"
                            strokeWidth="2"
                            stroke="currentColor"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M4 4m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                            <path d="M9 4v16"></path>
                            <path d="M14 10l2 2l-2 2"></path>
                        </svg>
                        <span className="sr-only">Close settings sidebar</span>
                    </button>
                    <h2 className="text-2xl font-medium">Settings</h2>
                </div>

                {/* Select */}
                <div className="px-2 py-3">
                    <h3 className="py-4 text-lg">Display settings</h3>
                    <label htmlFor="select-mode" className="text-sm">
                        Chat display mode
                    </label>
                    <select
                        onChange={(e) => changeChatMode(e.target.value)}
                        value={chatMode}
                        name="select-mode"
                        id="select-mode"
                        className="mt-2 w-full cursor-pointer rounded-lg border-r-4 border-transparent bg-slate-200 py-3 pl-1 focus:outline-none focus:ring-2 focus:ring-primary-300 dark:bg-slate-800">
                        <option value="Minimal">Minimal</option>
                        <option value="Bubbles">Bubbles</option>
                    </select>
                </div>
                {/* Toggle */}
                <div className="px-2 py-4">
                    <label className="relative flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            value={isDarkMode ? 1 : 0}
                            className="peer sr-only"
                            onChange={() => setIsDarkMode()}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-400 dark:border-slate-600 dark:bg-slate-700 dark:peer-focus:ring-primary-200"></div>
                        <span className="ml-3 text-sm">{isDarkMode ? 'Dark mode' : 'Light mode'}</span>
                    </label>
                </div>
                {/* Notifications toggle */}
                <div className="px-2 py-4">
                    <h3 className="py-4 text-lg">Experimental settings</h3>
                    <label className="relative flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={isChatNotificationsEnabled}
                            className="peer sr-only"
                            onChange={() => {
                                const updateState = !isChatNotificationsEnabled;

                                dispatch(setNotifications([]));
                                dispatch(setChatNotificationsState(updateState));
                            }}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-400 dark:border-slate-600 dark:bg-slate-700 dark:peer-focus:ring-primary-200"></div>
                        <span className="ml-3 text-sm">
                            {isChatNotificationsEnabled ? 'Enable notifications' : 'Disable notifications'}
                        </span>
                    </label>
                </div>

                {/* Enable QR codes */}
                <div className="px-2 py-4">
                    <label className="relative flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={isQRCodeEnabled}
                            className="peer sr-only"
                            onChange={() => {
                                const updateState = !isQRCodeEnabled;

                                // setIsQRCodeEnabled(updateState);
                                dispatch(setQRCodeLinksState(updateState));
                            }}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-400 dark:border-slate-600 dark:bg-slate-700 dark:peer-focus:ring-primary-200"></div>
                        <span className="ml-3 text-sm">
                            {isQRCodeEnabled ? 'Enable QR code links' : 'Disable QR code links'}
                        </span>
                    </label>
                </div>

                {/* Show chat list on load */}
                <div className="px-2 py-4">
                    <label className="relative flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={isHideChatListOnLoadEnabled}
                            className="peer sr-only"
                            onChange={() => {
                                const updateState = !isHideChatListOnLoadEnabled;

                                // setIsHideChatListOnLoadEnabled(updateState);
                                dispatch(setHideChatListOnLoadState(updateState));
                            }}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-400 dark:border-slate-600 dark:bg-slate-700 dark:peer-focus:ring-primary-200"></div>
                        <span className="ml-3 text-sm">Hide chat list on start up</span>
                    </label>
                </div>

                {/* Autohide chat list */}
                <div className="px-2 py-4">
                    <label className="relative flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={isAutohideChatListEnabled}
                            className="peer sr-only"
                            onChange={() => {
                                const updateState = !isAutohideChatListEnabled;

                                // setIsAutohideChatListEnabled(updateState);
                                dispatch(setAutohideChatListState(updateState));
                            }}
                        />
                        <div className="peer h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-300 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-400 dark:border-slate-600 dark:bg-slate-700 dark:peer-focus:ring-primary-200"></div>
                        <span className="ml-3 text-sm">Auto hide chat list</span>
                    </label>
                </div>
            </div>
        </aside>
    );
};
